import React,{useEffect,useState}from'react';
import*as FiIcons from'react-icons/fi';
import SafeIcon from'../common/SafeIcon';
import{questionTypes}from'../data';
import QuestionOptionEditor from'./QuestionOptionEditor';
import RepeatingFieldsEditor from'./RepeatingFieldsEditor';
import QuestionnaireVersionHistory from'./QuestionnaireVersionHistory';
import { formatEventDate } from '../utils/eventDisplay';
import'./questionnaire-builder.css';

const{
  FiArrowLeft,
  FiClock,
  FiCopy,
  FiEye,
  FiEyeOff,
  FiGripVertical,
  FiMoreHorizontal,
  FiPlus,
  FiTrash2
}=FiIcons;

const CANDLE_COUNT=13;

function createCandleQuestions(event){
  const details=[
    event?.name,
    event?.client,
    formatEventDate(event?.date),
    event?.venue
  ].filter(Boolean).join(' · ');

  return Array.from({length:CANDLE_COUNT},(_,index)=>({
    id:`candle-lighting-${index+1}`,
    title:`Candle ${String(index+1).padStart(2,'0')} — Name, introduction, and music`,
    type:'Repeating fields',
    required:false,
    visible:true,
    options:[],
    fields:[
      {id:'name',label:'Name',type:'Short text',required:false,options:[]},
      {id:'introduction',label:'How to Introduce',type:'Long text',required:false,options:[]},
      {id:'music',label:'Music',type:'Song entry',required:false,options:[]}
    ],
    help:details
      ?`Client event: ${details}`
      :'Add the participant name, introduction wording, and music.'
  }));
}

function QuestionAnswerEditor({item,onUpdate}){
  if(item.type==='Repeating fields'){
    return <RepeatingFieldsEditor question={item} onChange={onUpdate}/>;
  }
  return <QuestionOptionEditor question={item} onChange={onUpdate}/>;
}

function QuestionCard({item,index,onUpdate,onDelete,onDuplicate,onDragStart,onDrop}){
  const supportsAnswerEditing=['Single select','Multiple select','Dropdown','Yes / No','Repeating fields'].includes(item.type);
  return <article className="question-card" draggable onDragStart={onDragStart} onDragOver={(event)=>event.preventDefault()} onDrop={onDrop}>
    <div className="question-card-index"><SafeIcon icon={FiGripVertical}/><span>{String(index+1).padStart(2,'0')}</span></div>
    <div className="question-card-content">
      <label className="question-title-field"><span>Question label</span><input value={item.title} onChange={(event)=>onUpdate({...item,title:event.target.value})} aria-label={`Question ${index+1} label`}/></label>
      <div className="question-card-meta">
        <span className="question-type-pill">{item.type}</span><span>{item.required?'Required':'Optional'}</span>
        <span>{item.visible?'Visible to client':'Hidden from client'}</span>
        {supportsAnswerEditing&&<span className="answer-editable-pill">Answers editable</span>}
      </div>
    </div>
    <label className="question-type-field"><span>Answer type</span><select value={item.type} onChange={(event)=>onUpdate({...item,type:event.target.value})} aria-label={`Answer type for question ${index+1}`}>{questionTypes.map((type)=><option key={type}>{type}</option>)}</select></label>
    <div className="question-actions">
      <button type="button" className={`question-action ${item.required?'active':''}`} onClick={()=>onUpdate({...item,required:!item.required})} aria-label="Toggle required" title="Toggle required"><b>*</b></button>
      <button type="button" className="question-action" onClick={()=>onUpdate({...item,visible:!item.visible})} aria-label={item.visible?'Hide question':'Show question'} title={item.visible?'Hide question':'Show question'}><SafeIcon icon={item.visible?FiEye:FiEyeOff}/></button>
      <button type="button" className="question-action" onClick={onDuplicate} aria-label="Duplicate question" title="Duplicate question"><SafeIcon icon={FiCopy}/></button>
      <button type="button" className="question-action danger" onClick={onDelete} aria-label="Delete question" title="Delete question"><SafeIcon icon={FiTrash2}/></button>
    </div>
    {supportsAnswerEditing&&<QuestionAnswerEditor item={item} onUpdate={onUpdate}/>}
  </article>;
}

function CandleEventDetails({event}){
  const details=[['Event',event?.name],['Client',event?.client],['Date',formatEventDate(event?.date)],['Venue',event?.venue],['Guests',event?.guests]];
  return <div className="candle-event-details">{details.filter(([,value])=>value).map(([label,value])=><div key={label}><small>{label}</small><strong>{value}</strong></div>)}</div>;
}

function QuestionnaireBuilder({sections,setSections,preview,onBack,eventName,event}){
  const[active,setActive]=useState(sections[0]?.id);
  const[dragged,setDragged]=useState(null);
  const[draggedSection,setDraggedSection]=useState(null);
  const[showVersions,setShowVersions]=useState(false);
  const current=sections.find((section)=>section.id===active)||sections[0];

  useEffect(()=>{
    if(!sections.some((section)=>section.id==='candle-lighting')||!event)return;
    setSections((items)=>items.map((section)=>{
      if(section.id!=='candle-lighting'||section.questions.length)return section;
      return{
        ...section,
        questions:createCandleQuestions(event),
        description:`Candle lighting details for ${event.client||event.name||'this event'}${event.date?` · ${formatEventDate(event.date)}`:''}${event.venue?` · ${event.venue}`:''}.`
      };
    }));
  },[event?.id]);

  const updateSection=(section)=>setSections((items)=>items.map((item)=>item.id===section.id?section:item));
  const updateQuestion=(question)=>updateSection({...current,questions:current.questions.map((item)=>item.id===question.id?question:item)});
  const addQuestion=()=>updateSection({...current,questions:[...current.questions,{id:Date.now().toString(),title:'New question',type:'Short text',required:false,visible:true,options:[]}]});

  const deleteSection=()=>{
    if(sections.length===1){window.alert('Your questionnaire must have at least one section.');return;}
    if(!window.confirm(`Delete the “${current.title}” section and all of its questions? This action cannot be undone.`))return;
    const next=sections.filter((section)=>section.id!==current.id);
    setSections(next);
    setActive(next[0]?.id);
  };

  const dropQuestion=(targetId)=>{
    if(!dragged||dragged===targetId)return;
    const questions=[...current.questions];
    const from=questions.findIndex((item)=>item.id===dragged);
    const to=questions.findIndex((item)=>item.id===targetId);
    if(from<0||to<0)return;
    const[moved]=questions.splice(from,1);
    questions.splice(to,0,moved);
    updateSection({...current,questions});
    setDragged(null);
  };

  const dropSection=(targetId)=>{
    if(!draggedSection||draggedSection===targetId){setDraggedSection(null);return;}
    const next=[...sections];
    const from=next.findIndex((section)=>section.id===draggedSection);
    const to=next.findIndex((section)=>section.id===targetId);
    if(from<0||to<0){setDraggedSection(null);return;}
    const[moved]=next.splice(from,1);
    next.splice(to,0,moved);
    setSections(next);
    setDraggedSection(null);
  };

  const addSection=()=>{
    const section={id:Date.now().toString(),title:'New Section',description:'Add section guidance.',enabled:true,questions:[]};
    setSections((items)=>[...items,section]);
    setActive(section.id);
  };

  if(!current)return null;

  return <main className="main-content builder-page">
    <header className="builder-page-header">
      <div><span className="eyebrow">Client experience</span><h1>Edit client questionnaire</h1><p>{eventName?`Customize every question and client answer for ${eventName}.`:'Customize every question and answer clients can provide.'}</p></div>
      <div className="builder-header-actions">
        <button type="button" className="button secondary" onClick={onBack}><SafeIcon icon={FiArrowLeft}/>Back to event</button>
        <button type="button" className="button secondary" onClick={()=>setShowVersions(true)}><SafeIcon icon={FiClock}/>Version history</button>
        <button type="button" className="button primary" onClick={preview}><SafeIcon icon={FiEye}/>Preview questionnaire</button>
      </div>
    </header>
    <section className="builder-summary">
      <div><strong>{sections.length}</strong><span>Sections</span></div>
      <div><strong>{sections.reduce((total,section)=>total+section.questions.length,0)}</strong><span>Total questions</span></div>
      <div><strong>{sections.filter((section)=>section.enabled).length}</strong><span>Active sections</span></div>
      <div className="builder-status"><span className="status-dot"/><span>Changes saved locally</span></div>
    </section>
    <div className="builder-layout">
      <aside className="section-list panel">
        <div className="section-list-header"><div><span className="eyebrow">Structure</span><h2>Questionnaire sections</h2></div><button type="button" className="icon-btn" onClick={addSection} aria-label="Add section"><SafeIcon icon={FiPlus}/></button></div>
        <p className="section-list-hint"><SafeIcon icon={FiGripVertical}/>Drag sections to reorder them.</p>
        <nav className="section-list-items" aria-label="Questionnaire sections">
          {sections.map((section,index)=><button type="button" draggable className={`${active===section.id?'active ':''}${draggedSection===section.id?'section-dragging':''}`} key={section.id} onClick={()=>setActive(section.id)} onDragStart={(event)=>{setDraggedSection(section.id);event.dataTransfer.effectAllowed='move';}} onDragOver={(event)=>event.preventDefault()} onDrop={()=>dropSection(section.id)} onDragEnd={()=>setDraggedSection(null)}>
            <span className="section-number">{String(index+1).padStart(2,'0')}</span><span className="section-copy"><strong>{section.title}</strong><small>{section.questions.length} questions · {section.enabled?'Active':'Hidden'}</small></span><SafeIcon icon={FiMoreHorizontal}/>
          </button>)}
        </nav>
      </aside>
      <section className="builder-canvas panel">
        <div className="builder-canvas-header">
          <div className="builder-section-heading"><span className="section-number large">{String(sections.findIndex((section)=>section.id===current.id)+1).padStart(2,'0')}</span><div><span className="eyebrow">Editing section</span><input className="section-title-input" value={current.title} onChange={(event)=>updateSection({...current,title:event.target.value})} aria-label="Section title"/><input className="section-description-input" value={current.description} onChange={(event)=>updateSection({...current,description:event.target.value})} aria-label="Section description"/></div></div>
          <div className="builder-header-actions"><button type="button" className={`section-toggle ${current.enabled?'on':''}`} onClick={()=>updateSection({...current,enabled:!current.enabled})} aria-pressed={current.enabled}><span/>{current.enabled?'Active':'Hidden'}</button><button type="button" className="icon-btn danger" onClick={deleteSection} aria-label={`Delete ${current.title} section`}><SafeIcon icon={FiTrash2}/></button></div>
        </div>
        {current.id==='candle-lighting'&&<CandleEventDetails event={event}/>}
        <div className="questions-header"><div><h2>Questions and client answers</h2><p>Edit questions, choices, repeating fields, and music requests.</p></div><span className="question-count">{current.questions.length} total</span></div>
        <div className="questions">
          {current.questions.length===0?<div className="questions-empty"><SafeIcon icon={FiPlus}/><strong>This section is empty</strong><span>Add the first question to get started.</span></div>:current.questions.map((item,index)=><QuestionCard key={item.id} item={item} index={index} onUpdate={updateQuestion} onDragStart={()=>setDragged(item.id)} onDrop={()=>dropQuestion(item.id)} onDelete={()=>updateSection({...current,questions:current.questions.filter((question)=>question.id!==item.id)})} onDuplicate={()=>updateSection({...current,questions:[...current.questions,{...item,id:Date.now().toString(),title:`${item.title} copy`}]})}/>)}
        </div>
        <button type="button" className="add-question builder-add-question" onClick={addQuestion}><SafeIcon icon={FiPlus}/>Add question</button>
      </section>
    </div>
    {showVersions&&<QuestionnaireVersionHistory sections={sections} onRestore={(snapshot)=>{setSections(snapshot);setActive(snapshot[0]?.id);setShowVersions(false);}} onClose={()=>setShowVersions(false)}/>}
  </main>;
}

export default QuestionnaireBuilder;